import { DEFAULT_COLORS } from 'components/color-picker';
import { parseUTCdateTime } from 'utils/dates';
import type { MarketDataQuery } from 'pages/pricing/shared/types';

// The Price-Visualization view model: the draft/series shapes plus the pure functions that derive
// from them (query building, base-as-of resolution, horizon math). Kept out of the component so
// the logic is unit-testable in isolation.

export type ChartType = 'settles_forwards' | 'tenor' | 'forward';
export type Metric = 'price' | 'vol';
export type Mode = 'curve' | 'contract';

export interface VizSeries {
  id: number;
  name: string;
  color: string;
  mode: Mode;
  instrument: string; // String(curveId) or String(contractId)
  chartType: ChartType;
  metric: Metric;
  asOfDates: string[];
  addRisk: boolean;
  tenorStart: string; // YYYY-MM
  tenorEnd: string;
  fwdFrom: number;
  fwdTo: number;
}

// A view being edited: its name + global window (base as-of + months back/forward) + series.
export type AsOfMode = 'latest' | 'fixed';
export interface ViewDraft {
  name: string;
  asOfMode: AsOfMode; // 'latest' = newest available as-of; 'fixed' = the picked baseAsOf date
  baseAsOf: string; // the fixed as-of date (used when asOfMode === 'fixed')
  historical: number; // months of history to show before the base
  horizon: number; // months of forward projection after the base
  series: VizSeries[];
}

export const DEFAULT_HORIZON = 36;
export const DEFAULT_HISTORICAL = 12;

// A view starts named this; saving is blocked until the user renames it to something
// non-default and unique among their own saved views.
export const DEFAULT_VIEW_NAME = 'Untitled view';

// Shift an ISO (YYYY-MM-DD) date by N months (negative = back); returns YYYY-MM-DD.
export const shiftMonthsIso = (iso: string, months: number): string => {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
};

let seq = 0;
export const nextId = () => {
  seq += 1;
  return seq;
};

export const blankSeries = (baseAsOf: string): VizSeries => ({
  id: nextId(),
  name: 'Series 1',
  color: DEFAULT_COLORS[0],
  mode: 'curve',
  instrument: '',
  chartType: 'settles_forwards',
  metric: 'price',
  asOfDates: baseAsOf ? [baseAsOf] : [],
  addRisk: false,
  tenorStart: '',
  tenorEnd: '',
  fwdFrom: 1,
  fwdTo: 12,
});

export const snapshot = (d: ViewDraft) =>
  JSON.stringify({
    name: d.name,
    asOfMode: d.asOfMode,
    baseAsOf: d.baseAsOf,
    historical: d.historical,
    horizon: d.horizon,
    series: d.series,
  });

// Backend timestamps are UTC; format them in UTC so the date can't drift a day near midnight.
export const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  return parseUTCdateTime(iso);
};

// The concrete base as-of a draft resolves to: the picked date in fixed mode, else the newest
// available as-of date.
export const resolveBase = (d: ViewDraft, asOfDates: string[]): string => {
  const latest = asOfDates.length ? [...asOfDates].sort().slice(-1)[0] : '';
  return d.asOfMode === 'fixed' ? d.baseAsOf || latest : latest;
};

// baseAsOf + N months → the closing edge of the global horizon (for the details panel).
export const horizonEnd = (baseAsOf: string, horizon: number): string => {
  if (!baseAsOf) return '—';
  if (!horizon) return 'All';
  return shiftMonthsIso(baseAsOf, horizon);
};

// Build the market-data request from a draft. Pure (takes asOfDates), so it can live at module
// scope; it's memoized in the component to key the live react-query.
export const buildQuery = (
  d: ViewDraft,
  asOfDates: string[]
): MarketDataQuery => {
  const base = resolveBase(d, asOfDates);
  return {
    // Latest mode leaves the anchor to the backend's newest as-of; fixed pins it to `base`.
    end: d.asOfMode === 'fixed' ? base || null : null,
    // Historical window start (base − N months); the forward cap rides on horizonMonths.
    start: d.historical ? shiftMonthsIso(base, -d.historical) || null : null,
    horizonMonths: d.horizon || null,
    series: d.series
      .filter((s) => s.instrument)
      .map((s) => ({
        id: s.id,
        type:
          s.chartType === 'settles_forwards' ? ('sf' as const) : s.chartType,
        metric: s.metric,
        instrument:
          s.mode === 'curve'
            ? { kind: 'curve' as const, curveId: Number(s.instrument) }
            : { kind: 'contract' as const, contractId: Number(s.instrument) },
        asOf: s.chartType === 'settles_forwards' ? s.asOfDates : base,
        tenorStart: s.tenorStart || undefined,
        tenorEnd: s.tenorEnd || undefined,
        fwdFrom: s.fwdFrom,
        fwdTo: s.fwdTo,
      })),
  };
};
